// [S-062 / S-216] Template authoring screen. platform_admin (L1) or
// tenant_admin (L2) workflow:
//   1. Drag-drop / pick a DOCX/PDF                         → upload
//      (optional: "Auto-tag on upload" toggle pre-fills metadata)
//   2. Server returns parsed text (+ optional tag suggestion)
//   3. Edit in CKEditor (lazy-loaded; HTML round-trips)
//   4. Confirm metadata (deal type / guild / tier / anchor CBA) —
//      either manually or by clicking "Auto-tag from content"
//   5. Save as draft  → POST /(layer)/templates/draft
//   6. Certify        → POST /(layer)/templates/:id/certify
//
// S-216 invariant: Certify is gated until ALL FOUR taxonomy fields are
// EXPLICITLY set (no silent defaults). Initial state has every picker
// at `undefined` so the user has to either click each one or run
// Auto-tag. This kills the bug where a one-click certify on a fresh
// upload landed `feature_film / SAG / professional / null` regardless
// of the document.
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { TALENT_TIERS } from "../lib/talentTier";
import {
  Button,
  EditorFrame,
  PageHead,
  Select,
} from "../components";
import { getApiErrorMessage } from "../data/api";
import {
  useAutoTagL2Template,
  useCertifyL2Template,
  useSubmitL2Draft,
  type L2TemplateRow,
} from "../data/l2TemplatesQuery";
import {
  useAutoTagL1Template,
  useCertifyL1Template,
  useSubmitL1Draft,
  type TemplateTagSuggestion,
} from "../data/l1TemplatesQuery";

// A fresh client-minted draft id. The draft POST is create-by-id (same path
// clone uses), so a from-scratch template needs no upload to get an id — the
// blank editor opens immediately and the id lands server-side on first save.
function mintTemplateId(): string {
  return `tpl-new-${Date.now().toString(36)}-${Math.floor(
    Math.random() * 0xffffff
  )
    .toString(16)
    .padStart(6, "0")}`;
}

const DEAL_TYPES: ReadonlyArray<{
  value: L2TemplateRow["dealType"];
  label: string;
}> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{
  value: L2TemplateRow["guild"];
  label: string;
}> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS: ReadonlyArray<{
  value: L2TemplateRow["tier"];
  label: string;
}> = [
  ...TALENT_TIERS,
];

// Common CBA suggestions surfaced in the picker. Free-text is allowed
// so users can record CBAs we haven't seen — the field is a string,
// not an enum on the wire.
const ANCHOR_CBA_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "SAG-AFTRA 2026 Theatrical", label: "SAG-AFTRA 2026 Theatrical" },
  { value: "SAG-AFTRA 2026 TV", label: "SAG-AFTRA 2026 TV" },
  {
    value: "SAG-AFTRA 2026 Theatrical/Streaming",
    label: "SAG-AFTRA 2026 Theatrical/Streaming",
  },
  { value: "DGA Basic Agreement 2023", label: "DGA Basic Agreement 2023" },
  { value: "WGA MBA 2026", label: "WGA MBA 2026" },
  { value: "IATSE Basic Agreement 2021", label: "IATSE Basic Agreement 2021" },
];

export function TemplateAuthor(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();

  // Form state. Taxonomy fields are undefined-init: the user has to
  // pick (or run Auto-tag) before Certify enables. The draft id is minted
  // up front so the editor opens blank — no upload step (Import templates
  // owns the file path; a redundant upload here confused authors).
  const [draftId, setDraftId] = useState<string | null>(() => mintTemplateId());
  const [content, setContent] = useState<string>("");
  // Post-save affirmation: the saved draft id, or null. Cleared when the user
  // edits again so it never claims "saved" over unsaved changes. The
  // "incomplete tags" note is derived live from tagsComplete at render.
  const [saved, setSaved] = useState<string | null>(null);
  const [title, setTitle] = useState<string>("");
  const [dealType, setDealType] = useState<
    L2TemplateRow["dealType"] | undefined
  >(undefined);
  const [guild, setGuild] = useState<L2TemplateRow["guild"] | undefined>(
    undefined
  );
  const [tier, setTier] = useState<L2TemplateRow["tier"] | undefined>(
    undefined
  );
  const [anchorCBA, setAnchorCBA] = useState<string | undefined>(undefined);
  // Per-field rationale captured when auto-tag populates the form;
  // cleared when the user manually overrides a field so we don't show
  // a stale rationale next to a user-set value.
  const [rationale, setRationale] = useState<
    TemplateTagSuggestion["rationale"] | null
  >(null);
  const [confidence, setConfidence] = useState<number | null>(null);

  // Which library the draft targets is a SCOPE decision: platform staff (no own
  // studio → studioId null) author the global L1 catalog; anyone with a studio
  // authors their tenant's L2 library. Authority is a separate, capability
  // check (`template:author`) — so a tenant PERSONA (custom role) with that
  // capability authors L2 exactly like a tenant_admin, instead of being blocked
  // by a role-name gate.
  const layer: "L1" | "L2" = session?.studioId == null ? "L1" : "L2";
  const canAuthor = sessionCan(session, "template:author", { layer });

  const tenantId =
    layer === "L2" && session?.studioId != null
      ? `tenant-${session.studioId}`
      : "";

  // Mutations — instantiate both sets and pick the active one by layer.
  const submitDraftL1 = useSubmitL1Draft();
  const certifyL1 = useCertifyL1Template();
  const autoTagL1 = useAutoTagL1Template();
  const submitDraftL2 = useSubmitL2Draft(tenantId);
  const certifyL2 = useCertifyL2Template(tenantId);
  const autoTagL2 = useAutoTagL2Template(tenantId);

  const submitDraft = layer === "L1" ? submitDraftL1 : submitDraftL2;
  const certify = layer === "L1" ? certifyL1 : certifyL2;
  const autoTag = layer === "L1" ? autoTagL1 : autoTagL2;

  if (
    session === null ||
    !canAuthor ||
    (layer === "L2" && tenantId.length === 0)
  ) {
    return <Navigate to="/library" replace />;
  }

  // Applies a tag suggestion to the form, preserving any field the
  // user has already manually set. Fields only fill when empty so a
  // user who set "Guild=DGA" before clicking Auto-tag doesn't lose
  // their pick.
  const applyTagSuggestion = (s: TemplateTagSuggestion): void => {
    setTitle((prev) => (prev.length === 0 ? s.title : prev));
    setDealType((prev) => prev ?? s.dealType);
    setGuild((prev) => prev ?? s.guild);
    setTier((prev) => prev ?? s.tier);
    if (s.anchorCBA !== null) {
      setAnchorCBA((prev) => prev ?? s.anchorCBA ?? undefined);
    }
    setRationale(s.rationale);
    setConfidence(s.confidence);
  };

  const handleAutoTag = (): void => {
    if (content.trim().length === 0) return;
    autoTag.mutate(
      { content },
      {
        onSuccess: (s) => {
          applyTagSuggestion(s);
        },
      }
    );
  };

  // Certify gating: every taxonomy field must be explicitly set AND
  // content must be present. anchorCBA can be empty-string null per the
  // schema, but for the gate we require an explicit pick — the user
  // can pick "None" via free text if they really mean it (future
  // follow-up).
  const tagsComplete =
    title.trim().length > 0 &&
    dealType !== undefined &&
    guild !== undefined &&
    tier !== undefined &&
    anchorCBA !== undefined &&
    anchorCBA.length > 0;

  // Save a work-in-progress draft. Only content is required — a draft is by
  // definition incomplete, so unset taxonomy fields fall back to defaults the
  // author refines later (the full-tag gate is on Certify, not Save). The saved
  // draft lands in Template Management as a draft to finish + certify.
  const handleSaveDraft = (): void => {
    if (draftId === null || content.trim().length === 0) return;
    submitDraft.mutate(
      {
        id: draftId,
        title: title.trim().length > 0 ? title.trim() : null,
        dealType: dealType ?? "feature_film",
        guild: guild ?? "SAG",
        tier: tier ?? "professional",
        content,
        effectiveFrom: new Date().toISOString(),
        anchorCBA: anchorCBA ?? null,
      },
      {
        onSuccess: (t) => {
          setDraftId(t.id);
          setContent(t.content);
          setSaved(t.id);
        },
      }
    );
  };

  const handleCertify = async (): Promise<void> => {
    if (
      draftId === null ||
      content.trim().length === 0 ||
      !tagsComplete
    ) {
      return;
    }
    const now = new Date().toISOString();
    try {
      const saved = await submitDraft.mutateAsync({
        id: draftId,
        title: title.trim(),
        dealType: dealType,
        guild: guild,
        tier: tier,
        content,
        effectiveFrom: now,
        anchorCBA: anchorCBA ?? null,
      });
      setDraftId(saved.id);
      setContent(saved.content);
      await certify.mutateAsync({
        templateId: saved.id,
        effectiveFrom: now,
      });
      navigate(`/library/${encodeURIComponent(saved.id)}`);
    } catch {
      // Errors surface via the banners below.
    }
  };

  const submitError = getApiErrorMessage(submitDraft.error) ?? undefined;
  const certifyError = getApiErrorMessage(certify.error) ?? undefined;
  const autoTagError = getApiErrorMessage(autoTag.error) ?? undefined;

  // Per-field auto-tag rationale becomes each chip's hover tooltip.
  const chipTitle = (r: string | undefined): string | undefined =>
    r !== undefined && r.length > 0 ? `↳ ${r}` : undefined;

  // Auto-tag (esp. Gemini) can return a CBA not in the fixed list — or any
  // value for an AFM/PGA template — so surface it as a selectable option
  // instead of the chip rendering blank while the certify gate passes.
  const anchorCBAOptions =
    anchorCBA !== undefined &&
    !ANCHOR_CBA_OPTIONS.some((o) => o.value === anchorCBA)
      ? [
          ...ANCHOR_CBA_OPTIONS,
          { value: anchorCBA, label: `${anchorCBA} (auto-tagged)` },
        ]
      : ANCHOR_CBA_OPTIONS;

  return (
    <div className="doc-route">
      {/* Same shell as the draft-edit screen: page chrome + one sticky meta
          bar (TemplateEdit's .tpl-author--edit) over a full-height editor. */}
      <div className="doc-route__chrome tpl-author tpl-author--edit">
        <PageHead
          eyebrow={`Templates · new ${layer} template`}
          title={
            <>
              Author a <em>certified</em> {layer} template.
            </>
          }
          sub={
            layer === "L1"
              ? "Draft a new L1 template from scratch, confirm the taxonomy tags (pick each or auto-tag from your draft), then certify. To bring in an existing document instead, use Import templates. L1 templates are global certified standards for every organization."
              : "Draft a new L2 template from scratch, confirm the taxonomy tags (pick each or auto-tag from your draft), then certify. To bring in an existing document instead, use Import templates. Once certified, they appear in your studio's Templates."
          }
        />

        {/* One slim sticky meta bar: title inline + four taxonomy chips +
            the auto-tag / save / certify actions, all pinned. Unset required
            chips read dashed + amber; the S-216 certify gate is unchanged. */}
        <div className="tpl-author__meta">
          <span className="tpl-author__meta__lbl">Metadata</span>

          <div className="tpl-author__meta__row tpl-author__meta__row--title">
            <input
              type="text"
              className="tpl-author__title-input"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setRationale((r) => (r === null ? r : { ...r, title: "" }));
                setSaved(null);
              }}
              placeholder="Template title — e.g. Theatrical Actor Deal Memo (SAG-AFTRA)"
              data-testid="tpl-title-input"
            />
          </div>

          <div className="tpl-author__meta__row">
            <label>Deal type</label>
            <span
              className={
                dealType === undefined ? "tpl__filter is-required" : "tpl__filter"
              }
              title={chipTitle(rationale?.dealType)}
              data-testid="tpl-chip-dealType"
            >
              <Select
                label="Deal type"
                value={dealType}
                options={DEAL_TYPES}
                onValueChange={(v) => {
                  if (v !== undefined) {
                    setDealType(v);
                    setRationale((r) => (r === null ? r : { ...r, dealType: "" }));
                    setSaved(null);
                  }
                }}
                clearable={false}
                triggerClassName="tpl__filter__select"
                placeholder="Set"
              />
            </span>
          </div>

          <div className="tpl-author__meta__row">
            <label>Guild</label>
            <span
              className={guild === undefined ? "tpl__filter is-required" : "tpl__filter"}
              title={chipTitle(rationale?.guild)}
              data-testid="tpl-chip-guild"
            >
              <Select
                label="Guild"
                value={guild}
                options={GUILDS}
                onValueChange={(v) => {
                  if (v !== undefined) {
                    setGuild(v);
                    setRationale((r) => (r === null ? r : { ...r, guild: "" }));
                    setSaved(null);
                  }
                }}
                clearable={false}
                triggerClassName="tpl__filter__select"
                placeholder="Set"
              />
            </span>
          </div>

          <div className="tpl-author__meta__row">
            <label>Tier</label>
            <span
              className={tier === undefined ? "tpl__filter is-required" : "tpl__filter"}
              title={chipTitle(rationale?.tier)}
              data-testid="tpl-chip-tier"
            >
              <Select
                label="Tier"
                value={tier}
                options={TIERS}
                onValueChange={(v) => {
                  if (v !== undefined) {
                    setTier(v);
                    setRationale((r) => (r === null ? r : { ...r, tier: "" }));
                    setSaved(null);
                  }
                }}
                clearable={false}
                triggerClassName="tpl__filter__select"
                placeholder="Set"
              />
            </span>
          </div>

          <div className="tpl-author__meta__row">
            <label>Anchor CBA</label>
            <span
              className={
                anchorCBA === undefined || anchorCBA.length === 0
                  ? "tpl__filter is-required"
                  : "tpl__filter"
              }
              title={chipTitle(rationale?.anchorCBA)}
              data-testid="tpl-chip-anchorCBA"
            >
              <Select<string>
                label="Anchor CBA"
                value={anchorCBA}
                options={anchorCBAOptions}
                onValueChange={(v) => {
                  if (v !== undefined) {
                    setAnchorCBA(v);
                    setRationale((r) => (r === null ? r : { ...r, anchorCBA: "" }));
                    setSaved(null);
                  }
                }}
                clearable={false}
                triggerClassName="tpl__filter__select"
                placeholder="Set"
              />
            </span>
          </div>

          <div className="tpl-author__actions">
            <Button
              variant="secondary"
              className="btn--s"
              onClick={handleAutoTag}
              disabled={autoTag.isPending || content.trim().length === 0}
              data-testid="tpl-auto-tag"
              title="Ask Claude to suggest deal type / guild / tier / anchor CBA from the template content"
            >
              {autoTag.isPending ? "Tagging…" : "Auto-tag from content"}
            </Button>
            <Button
              variant="ghost"
              className="btn--s"
              onClick={handleSaveDraft}
              disabled={submitDraft.isPending || content.trim().length === 0}
              data-testid="tpl-save-draft"
              title="Save a work-in-progress draft — you can finish the tags later. Certifying needs all four."
            >
              {submitDraft.isPending ? "Saving…" : "Save as draft"}
            </Button>
            <Button
              variant="primary"
              className="btn--s"
              onClick={() => {
                void handleCertify();
              }}
              disabled={
                certify.isPending ||
                submitDraft.isPending ||
                content.trim().length === 0 ||
                !tagsComplete
              }
              data-testid="tpl-certify"
              title={
                tagsComplete
                  ? undefined
                  : "All four taxonomy tags must be set before certifying."
              }
            >
              {!tagsComplete
                ? "Confirm tags to certify →"
                : submitDraft.isPending
                  ? "Saving draft…"
                  : certify.isPending
                    ? "Certifying…"
                    : "Certify template"}
            </Button>
          </div>
        </div>

        {confidence !== null && (
          <div className="tpl-author__confidence" data-testid="tpl-auto-confidence">
            Auto-tag confidence <b>{Math.round(confidence * 100)}%</b> — hover
            any chip for the reasoning; override anything that looks wrong.
          </div>
        )}

        {autoTagError !== undefined && (
          <div className="tpl-author__warn" role="alert">
            Auto-tag failed — {autoTagError}. Pick the tags manually.
          </div>
        )}

        {saved !== null && (
          <div className="tpl-author__saved" role="status" data-testid="tpl-saved">
            <span className="tpl-author__saved__msg">
              <span className="tpl-author__saved__check" aria-hidden="true">
                ✓
              </span>
              Draft saved — it's in Template Management as a draft.
              {!tagsComplete
                ? " Classification is incomplete (defaults used where blank); set the tags before certifying."
                : ""}
            </span>
            <Link to="/template-management" className="btn btn--s">
              View in Template Management →
            </Link>
          </div>
        )}

        {submitError !== undefined && (
          <div className="tpl-author__err" role="alert">
            Save failed — {submitError}
          </div>
        )}
        {certifyError !== undefined && (
          <div className="tpl-author__err" role="alert">
            Certify failed — {certifyError}
          </div>
        )}
      </div>

      {/* Full-height editor — the point of the screen (no `bounded`). */}
      <EditorFrame
        value={content}
        onChange={(next) => {
          setContent(next);
          setSaved(null);
        }}
        placeholder="Start typing the template here, or paste clauses to build from. To import an existing DOCX/PDF instead, use Import templates."
        ariaLabel="Template content editor"
        pagerMeta="New template · draft"
      />
    </div>
  );
}
